import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import RecapHeader from '../components/recap/RecapHeader';
import RecapMVP from '../components/recap/RecapMVP';
import RecapPlayerCard from '../components/recap/RecapPlayerCard';
import RecapShareButton from '../components/recap/RecapShareButton';
import RecapEmptyState from '../components/recap/RecapEmptyState';
import { formatDateEs } from '../components/recap/recapUtils';
import * as analytics from '../lib/analytics';

interface TagEntry { tag: string; count: number; type: 'positive' | 'negative'; }
interface PlayerTagBlock {
  playerId: string;
  name: string;
  avatar: string | null;
  isSubstitute: boolean;
  topTags: TagEntry[];
}
interface PublicRecap {
  matchId: string;
  status: string;
  location: string;
  date: string;
  time: string;
  team1: { name: string; color: string; goals: number };
  team2: { name: string; color: string; goals: number };
  mvp: { name: string; votes: number } | null;
  topTagsByPlayer: PlayerTagBlock[];
  completion: { reviewerCount: number; titularCount: number; percentage: number; ready: boolean };
}

const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#0E1110',
  color: '#fff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingBottom: 32,
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 480,
  background: '#181A1B',
  borderRadius: 16,
  margin: '16px 12px',
  overflow: 'hidden',
};

export default function PublicMatchRecapPage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error'; recap: PublicRecap | null; error: string | null }>({
    status: 'loading', recap: null, error: null
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`/api/matches/${id}/recap/public`);
        if (!res.ok) {
          if (!cancelled) setState({ status: 'error', recap: null, error: res.status === 404 ? 'NOT_FOUND' : 'GENERIC' });
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setState({ status: 'ready', recap: data, error: null });
          analytics.track('recap_view', {
            matchId: data.matchId,
            ready: data.completion?.ready === true,
          });
        }
      } catch {
        if (!cancelled) setState({ status: 'error', recap: null, error: 'GENERIC' });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (state.status === 'loading') {
    return <div style={containerStyle}><p style={{ marginTop: 64, color: 'rgba(255,255,255,0.6)' }}>Cargando…</p></div>;
  }

  if (state.status === 'error') {
    const msg = state.error === 'NOT_FOUND' ? 'Partido no encontrado' : 'No pudimos cargar el recap';
    return (
      <div style={containerStyle}>
        <p style={{ marginTop: 64, color: 'rgba(255,255,255,0.7)' }}>{msg}</p>
      </div>
    );
  }

  const recap = state.recap!;
  const isReady = recap.completion.ready;
  const visiblePlayers = recap.topTagsByPlayer.filter(p => !p.isSubstitute);

  return (
    <div style={containerStyle}>
      <div style={cardStyle} id="recap-card">
        <RecapHeader
          location={recap.location}
          date={recap.date}
          team1={recap.team1}
          team2={recap.team2}
        />
        {!isReady ? (
          <RecapEmptyState />
        ) : (
          <>
            {recap.mvp && (
              <RecapMVP name={recap.mvp.name} votes={recap.mvp.votes} avatar={null} />
            )}
            <div style={{ marginTop: 16 }}>
              {visiblePlayers.map((p) => (
                <RecapPlayerCard
                  key={p.playerId}
                  matchId={recap.matchId}
                  playerId={p.playerId}
                  name={p.name}
                  avatar={p.avatar}
                  topTags={p.topTags}
                  onReportAutoHidden={() => window.location.reload()}
                />
              ))}
            </div>
            <RecapShareButton dateLabel={formatDateEs(recap.date)} matchId={recap.matchId} />
          </>
        )}
      </div>
    </div>
  );
}
